import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Dices, Play, Info, X, RotateCcw, Check, Trophy } from 'lucide-react';
import { api, type MediaItem } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface SurpriseMeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMedia: (id: string) => void;
  onPlayMedia?: (id: string) => void;
}

export function SurpriseMeModal({
  isOpen,
  onClose,
  onSelectMedia,
  onPlayMedia,
}: SurpriseMeModalProps) {
  const { language, t } = useThemeLanguage();
  const [candidates, setCandidates] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState<MediaItem | null>(null);
  const spinTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setWinner(null);
      setIsSpinning(false);
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
      return;
    }

    // Load pool of items from home feed or list
    api.listMedia({ limit: '40', sortBy: 'popularity', sortOrder: 'desc' })
      .then((res) => {
        const pool = (res.items || []).filter((it: any) => it.posterPath || it.backdropPath);
        if (pool.length > 0) {
          setCandidates(pool);
          startSpin(pool);
        }
      })
      .catch(() => {});

    return () => {
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    };
  }, [isOpen]);

  const startSpin = (pool: MediaItem[]) => {
    if (pool.length === 0) return;
    setIsSpinning(true);
    setWinner(null);

    let speed = 60; // initial speed in ms
    let counter = 0;
    const totalSpins = 28 + Math.floor(Math.random() * 12); // random stop point

    const tick = () => {
      counter++;
      setCurrentIndex((prev) => (prev + 1) % pool.length);

      // Deceleration curve
      if (counter > totalSpins - 15) {
        speed += 35;
      } else if (counter > totalSpins - 6) {
        speed += 90;
      }

      if (counter >= totalSpins) {
        // Pick final winner
        const chosen = pool[Math.floor(Math.random() * pool.length)] || pool[0] || null;
        setWinner(chosen);
        setIsSpinning(false);
      } else {
        spinTimerRef.current = setTimeout(tick, speed);
      }
    };

    spinTimerRef.current = setTimeout(tick, speed);
  };

  if (!isOpen) return null;

  const currentItem = winner || candidates[currentIndex];
  const isHebrew = language === 'he';
  const displayTitle = currentItem
    ? (isHebrew && currentItem.titleHe ? currentItem.titleHe : (currentItem.title || currentItem.originalTitle || ''))
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl animate-in fade-in duration-300">
      <div className="relative w-full max-w-lg rounded-3xl bg-neutral-900/95 border border-white/15 shadow-[0_0_60px_rgba(229,9,20,0.3)] p-6 md:p-8 text-white overflow-hidden text-center">
        {/* Ambient Top Glow */}
        <div className="absolute -top-20 inset-x-0 mx-auto w-48 h-48 bg-[#E50914]/30 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-neutral-400 hover:text-white transition-all cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Title */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <Dices className={`w-6 h-6 text-[#E50914] ${isSpinning ? 'animate-spin' : ''}`} />
          <h2 className="text-2xl font-black tracking-tight text-white">
            {isHebrew ? 'רולטת הקולנוע של Homeflix' : 'Cinema Roulette'}
          </h2>
        </div>
        <p className="text-xs text-neutral-400 mb-6">
          {isSpinning
            ? (isHebrew ? 'מערבב ובוחר עבורך את הכותר המושלם להערב...' : 'Finding your perfect match for tonight...')
            : (isHebrew ? 'יש לנו התאמה מושלמת עבורך!' : 'We found a great title for you!')}
        </p>

        {/* Poster Showcase */}
        <div className="relative mx-auto w-[180px] md:w-[220px] aspect-[2/3] rounded-2xl overflow-hidden bg-black border-2 border-white/20 shadow-2xl transition-all duration-300">
          {currentItem && (
            <img
              key={currentItem.id}
              src={currentItem.posterPath || currentItem.backdropPath || ''}
              alt={displayTitle}
              className={`w-full h-full object-cover transition-all ${
                isSpinning ? 'scale-105 blur-[1px]' : 'scale-100 blur-0'
              }`}
            />
          )}

          {/* Winner Glow Banner */}
          {winner && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-3 pt-6 animate-in fade-in duration-300">
              <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-widest text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-500/40 uppercase">
                <Trophy className="w-3 h-3" />
                {isHebrew ? 'הבחירה הנבחרת' : 'WINNER PICK'}
              </span>
            </div>
          )}

          {/* Spinning Overlay Indicator */}
          {isSpinning && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 rounded-full border-4 border-t-[#E50914] border-white/20 animate-spin" />
            </div>
          )}
        </div>

        {/* Title & Metadata */}
        {currentItem && (
          <div className="mt-5 space-y-2">
            <h3 className="text-lg md:text-xl font-black text-white truncate px-4">
              {displayTitle}
            </h3>
            {currentItem.year && (
              <p className="text-xs text-neutral-400 font-semibold">
                {currentItem.year} • {currentItem.genres?.slice(0, 2).join(', ')}
              </p>
            )}
            {winner && currentItem.overview && (
              <p className="text-xs text-neutral-300 line-clamp-2 px-4 leading-relaxed mt-2 animate-in fade-in">
                {isHebrew && currentItem.overviewHe ? currentItem.overviewHe : currentItem.overview}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-6">
          {winner ? (
            <>
              <button
                onClick={() => {
                  if (onPlayMedia) {
                    onPlayMedia(winner.id);
                  } else {
                    onSelectMedia(winner.id);
                  }
                  onClose();
                }}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E50914] hover:bg-[#b80710] text-white font-bold text-sm shadow-xl shadow-red-600/30 active:scale-95 transition-all cursor-pointer"
              >
                <Play className="w-4 h-4 fill-current" />
                <span>{isHebrew ? 'צפה עכשיו' : 'Watch Now'}</span>
              </button>

              <button
                onClick={() => {
                  onSelectMedia(winner.id);
                  onClose();
                }}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm backdrop-blur-md transition-all border border-white/15 cursor-pointer active:scale-95"
              >
                <Info className="w-4 h-4" />
                <span>{isHebrew ? 'מידע נוסף' : 'More Info'}</span>
              </button>

              <button
                onClick={() => startSpin(candidates)}
                className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/15 cursor-pointer active:scale-90 transition-all"
                title={isHebrew ? 'סובב שוב' : 'Spin Again'}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              disabled
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-neutral-800 text-neutral-500 font-bold text-sm"
            >
              <Sparkles className="w-4 h-4 animate-spin" />
              <span>{isHebrew ? 'הרולטה מסתובבת...' : 'Rolling...'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
